import { useQuery, useMutation } from '@apollo/client'
import type { User } from 'api/data.types'
import {
  addToFavoritesMutation,
  removeFromFavoritesMutation
} from 'api/mutations'
import { getIsFavoriteBook } from 'api/queries'
import { useToast } from 'utils/hooks/use-toast/useToast'
import type { ButtonToggleFavoriteProps } from './ButtonToggleFavorite.types'
import { favoriteVisibilityFragment } from './ButtonToggleFavorite.constants'

export const useToggleFavorite = ({ slug }: ButtonToggleFavoriteProps) => {
  const { toastMessage } = useToast()
  const {
    loading: loadingIsFavorite,
    data,
    refetch: updateIsFavorite
  } = useQuery<{ me: User }>(getIsFavoriteBook, {
    variables: { slug },
    fetchPolicy: 'network-only'
  })

  const [add, { loading: loadingAddition }] = useMutation(
    addToFavoritesMutation,
    {
      variables: { slug }
    }
  )

  const [remove, { loading: loadingRemotion }] = useMutation(
    removeFromFavoritesMutation,
    {
      variables: { slug },
      update(cache) {
        cache.writeFragment({
          id: `BookAsNode:${slug}`,
          fragment: favoriteVisibilityFragment,
          data: {
            favoritesVisibility: false
          }
        })
      }
    }
  )

  const [undo] = useMutation(addToFavoritesMutation, {
    variables: { slug },
    update(cache) {
      cache.writeFragment({
        id: `BookAsNode:${slug}`,
        fragment: favoriteVisibilityFragment,
        data: {
          favoritesVisibility: true
        }
      })
    }
  })

  const undoToast = async () => {
    try {
      await undo()
      toastMessage({
        message: 'Ação desfeita com sucesso'
      })
    } catch (err) {
      toastMessage({
        message: 'Não foi possível desfazer a ação',
        type: 'error'
      })
    }
  }

  const addToFavorites = async () => {
    try {
      await add()
      updateIsFavorite()
    } catch (err) {
      toastMessage({
        message:
          'Oops, aconteceu um erro: o livro não foi adicionado aos favoritos!',
        type: 'error'
      })
    }
  }

  const removeFromFavorites = async () => {
    try {
      await remove()
      updateIsFavorite()
      toastMessage({
        message: 'Conteúdo removido de favoritos com sucesso',
        textButton: {
          onClick: () => undoToast(),
          text: 'Desfazer'
        }
      })
    } catch (err) {
      toastMessage({
        message: 'Não foi possível remover o livro de favoritos',
        type: 'error'
      })
    }
  }

  return {
    loading: loadingIsFavorite || loadingAddition || loadingRemotion,
    isFavorite: data?.me?.reader?.isFavoriteBook,
    addToFavorites,
    removeFromFavorites
  }
}
